/*
 *  This file hrone-dialog.component.ts is part of HROne. The intellectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

import {
  AfterViewChecked,
  Component,
  ContentChild,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'hrone-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hrone-dialog.component.html',
  styleUrls: ['./hrone-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HroneDialogComponent implements OnChanges, AfterViewChecked, OnDestroy {

  // ── Inputs ────────────────────────────────────────────────────────────────

  /** Controls dialog visibility. Use [(visible)] for two-way binding. */
  @Input() visible: boolean = false;

  /** Header text. Ignored when a #header ng-template is projected. */
  @Input() header: string = '';

  /** Whether to render a backdrop overlay. Accepts boolean or truthy string. */
  @Input() modal: boolean | string = true;

  /** Whether to show the built-in close (×) button in the header. */
  @Input() closable: boolean = true;

  /** Close the dialog when the Escape key is pressed. */
  @Input() closeOnEscape: boolean = true;

  /** Close the dialog when the backdrop mask is clicked. */
  @Input() dismissableMask: boolean = false;

  /** Allow the dialog to be dragged by its header. */
  @Input() draggable: boolean = false;

  /** Allow the dialog to be resizable. */
  @Input() resizable: boolean = false; 

  /** Where the dialog is positioned relative to the viewport. */
  @Input() position: 'center' | 'top' | 'bottom' | 'left' | 'right' = 'center';

  /** Inline styles applied to the dialog container (e.g. { width: '500px' }). */
  @Input() style: Record<string, string> = {};

  /** Extra CSS class(es) applied to the dialog container element. */
  @Input() styleClass: string = '';

  /** Extra CSS class(es) applied to the backdrop mask element. */
  @Input() maskStyleClass: string = '';

  /** Base z-index of the mask layer. */
  @Input() baseZIndex: number = 1000;

  /** Trap keyboard focus inside the dialog while it is open. */
  @Input() focusTrap: boolean = false;

  /** Focus the first focusable element inside the dialog when it opens. */
  @Input() focusOnShow: boolean = false;

  /** Show a maximize / restore button in the header. */
  @Input() maximizable: boolean = false;

  /** Minimum Y coordinate (px from top of viewport) when dragging. */
  @Input() minY: number = 0;

  /**
   * When set to 'body' (default), the host element is physically moved to
   * document.body on init — escaping any parent stacking context. This is
   * required for correct z-index stacking when multiple dialogs are open.
   */
  @Input() appendTo: string = 'body';

  /** Whether to show the dialog header section (title + icons row). */
  @Input() showHeader: boolean = true;

  /** Whether to show the dialog footer section. */
  @Input() showFooter: boolean = true;

  /** Prevent body from scrolling while the dialog is open. Handled via CSS (body:has(>.p-dialog-mask)). Accepts boolean or string 'true'/'false'. */
  @Input() blockScroll: boolean | string = true;

  // ── Outputs ───────────────────────────────────────────────────────────────

  /** Emitted when visibility changes — enables [(visible)] two-way binding. */
  @Output() readonly visibleChange = new EventEmitter<boolean>();

  /** Emitted after the dialog becomes visible. */
  @Output() readonly onShow = new EventEmitter<void>();

  /** Emitted after the dialog is hidden. */
  @Output() readonly onHide = new EventEmitter<void>();

  // ── Content slots ─────────────────────────────────────────────────────────

  /** Custom header template: <ng-template #header>…</ng-template> */
  @ContentChild('header', { descendants: false }) headerTemplate?: TemplateRef<unknown>;

  /** Custom footer template: <ng-template #footer>…</ng-template> */
  @ContentChild('footer', { descendants: false }) footerTemplate?: TemplateRef<unknown>;

  // ── View refs ─────────────────────────────────────────────────────────────

  @ViewChild('dialogEl') dialogEl!: ElementRef<HTMLElement>;

  /** Reference to the mask overlay div — moved to body when dialog opens. */
  @ViewChild('maskEl') private maskEl?: ElementRef<HTMLElement>;

  // ── Internal state ────────────────────────────────────────────────────────

  isMaximized = false;

  /** Resolved z-index assigned when this dialog becomes visible. */
  computedZIndex = 0;

  /** True while the leave animation is playing — keeps the element in DOM during close. */
  isClosing = false;

   /** No-op — z-index is managed automatically. Accepted to avoid binding errors on migrated p-dialog templates. */
  @Input() autoZIndex: boolean | string = true;

  private static readonly CLOSE_ANIMATION_MS = 150;

  /** Set to true when visible becomes true; cleared in ngAfterViewChecked once maskEl is ready. */
  private _pendingAppend = false;

  /** id of close()'s pending cleanup setTimeout, so a reopen/external-close mid-animation can cancel it. */
  private closeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private dragOffset = { x: 0, y: 0 };
  private dragStart = { mouseX: 0, mouseY: 0, offsetX: 0, offsetY: 0, naturalTop: 0 };
  private readonly dragUnlisteners: (() => void)[] = [];

  // ── DI ───────────────────────────────────────────────────────────────────

  private readonly renderer = inject(Renderer2);
  private readonly el = inject(ElementRef);

  // ── Computed class strings ────────────────────────────────────────────────

  get maskCombinedClass(): string {
    // hrone-dialog-owned: marker class so the enter/leave-transition CSS (see
    // .scss) only ever applies to this component's own mask/dialog, never to
    // a native PrimeNG p-dialog/p-confirmDialog sharing the same .p-dialog-mask
    // class name elsewhere in the app.
    const classes: string[] = ['p-dialog-mask', 'p-component-overlay', 'p-overlay-mask', 'hrone-dialog-owned'];
    if (this.position !== 'center') {
      classes.push(`p-dialog-mask--${this.position}`);
    }
    if (!this.isModal) {
      classes.push('p-dialog-mask--no-backdrop');
    }
    if (!this.shouldBlockScroll) {
      classes.push('hrone-dialog-scrollable');
    }
    if (this.maskStyleClass) {
      classes.push(...this.maskStyleClass.split(' ').filter(Boolean));
    }
    if (this.appendTo !== 'body') {
      classes.push('hrone-dialog-mask--inline');
    }
    return classes.join(' ');
  }

  get dialogCombinedClass(): string {
    const classes: string[] = ['p-dialog', 'p-component'];
    if (this.position !== 'center') {
      classes.push(`p-dialog--position-${this.position}`);
    }
    if (this.styleClass) {
      classes.push(...this.styleClass.split(' ').filter(Boolean));
    }
    if (this.isMaximized) {
      classes.push('p-dialog--maximized');
    }
    if (this.draggable) {
      classes.push('p-dialog--draggable');
    }

    return classes.join(' ');
  }

  get isModal(): boolean {
    if (typeof this.modal === 'boolean') return this.modal;
    return this.modal !== 'false' && this.modal !== '';
  }

  get shouldBlockScroll(): boolean {
    return this.blockScroll === true || this.blockScroll === 'true';
  }

  get shouldRenderHeader(): boolean {
    return this.showHeader && !!(this.headerTemplate || this.header || this.closable || this.maximizable);
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['visible']) return;

    const isNowVisible = changes['visible'].currentValue === true;
    const wasVisible = changes['visible'].previousValue === true;

    if (isNowVisible && !wasVisible) {
      // Reopened while close()'s cleanup timer was still pending (e.g. Escape/mask close
      // followed by an external reopen before the leave animation finished) — cancel it
      // so it can't fire later and rip the mask back out of / force-close this new open.
      this.cancelPendingClose();

      // Query DOM for the current highest z-index across all open overlays (drawers + dialogs)
      // before this mask is appended, so it won't count itself.
      this.computedZIndex = this.getNextZIndex();

      // Reset drag transform for a clean open
      this.dragOffset = { x: 0, y: 0 };
      this.isMaximized = false;

      // ngAfterViewChecked will finish the open once @if has rendered the mask into the DOM
      this._pendingAppend = true;
    } else if (!isNowVisible && wasVisible) {
      // computedZIndex === 0 means close() already ran cleanup — skip to avoid double-emit
      if (this.computedZIndex === 0) return;

      // External close: parent set visible=false directly, possibly while close()'s own
      // cleanup timer was still pending (e.g. a redundant close via a different path) —
      // cancel it so it can't fire later and re-emit visibleChange/onHide a second time.
      this.cancelPendingClose();

      // External close: parent set visible=false directly (custom button etc.)
      // Restore mask to host so Angular's @if view destruction removes it from document.body
      const mask = this.maskEl?.nativeElement;
      if (mask?.parentElement === document.body) {
        this.el.nativeElement.appendChild(mask);
      }

      this.computedZIndex = 0;
      this.cleanupDragListeners();
      this.onHide.emit();
    }
  }

  ngAfterViewChecked(): void {
    if (this._pendingAppend && this.maskEl?.nativeElement) {
      this._pendingAppend = false;
      const mask = this.maskEl.nativeElement;
      // Move only the mask div to body — host element stays in place
      if (this.appendTo === 'body') {
        document.body.appendChild(mask);
      }
      // Fix for a rare broken/ghosted-render bug (see .scss for full history):
      // force a synchronous reflow so the browser commits the mask/dialog's
      // "closed" starting state before flipping to the "-active" class below
      // — required for the CSS `transition` to actually animate the value
      // change, matching how real PrimeNG drives its enter transition.
      void mask.offsetHeight;
      mask.classList.add('p-dialog-mask-active');
      mask.querySelector<HTMLElement>('.p-dialog')?.classList.add('p-dialog-active');
      this.applyDragTransform();
      if (this.focusOnShow) {
        this.focusFirstElement();
      }
      this.onShow.emit();
    }
  }

  ngOnDestroy(): void {
    this.cancelPendingClose();
    if (this.visible) {
      this.computedZIndex = 0;
      // If mask was moved to body, remove it manually since @if won't fire on destroy
      const mask = this.maskEl?.nativeElement;
      if (mask?.parentElement === document.body) {
        document.body.removeChild(mask);
      }
    }
    this.cleanupDragListeners();
  }

  // ── Keyboard handlers ─────────────────────────────────────────────────────

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.visible && this.closeOnEscape) {
      this.close();
    }
  }

  @HostListener('document:keydown.tab', ['$event'])
  onTabKey(event: Event): void {
    if (!this.visible || !this.focusTrap || !this.dialogEl) return;

    const e = event as KeyboardEvent;
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // ── Public actions ────────────────────────────────────────────────────────

  close(): void {
    if (this.isClosing) return;
    this.isClosing = true;
    // Drop the "-active" class in the same tick as isClosing so the
    // `.p-dialog-closing`/`.p-dialog-mask-closing` rules (added via the
    // isClosing template binding) unambiguously control the leave
    // transition's value/duration. See .scss for the bug this fixes.
    const openMask = this.maskEl?.nativeElement;
    openMask?.classList.remove('p-dialog-mask-active');
    openMask?.querySelector<HTMLElement>('.p-dialog')?.classList.remove('p-dialog-active');
    this.closeTimeoutId = setTimeout(() => {
      this.closeTimeoutId = null;
      // Restore mask to host so Angular's @if view destruction removes it from document.body
      const mask = this.maskEl?.nativeElement;
      if (mask?.parentElement === document.body) {
        this.el.nativeElement.appendChild(mask);
      }
      this.isClosing = false;
      this.computedZIndex = 0;
      this.cleanupDragListeners();
      this.visible = false;
      this.visibleChange.emit(false);
      this.onHide.emit();
    }, HroneDialogComponent.CLOSE_ANIMATION_MS);
  }

  onMaskClick(event: MouseEvent): void {
    if (this.dismissableMask && event.target === this.maskEl?.nativeElement) {
      this.close();
    }
  }

  toggleMaximize(): void {
    this.isMaximized = !this.isMaximized;
  }

  // ── Drag support ──────────────────────────────────────────────────────────

  onHeaderMouseDown(event: MouseEvent): void {
    if (!this.draggable || !this.dialogEl) return;
    event.preventDefault();

    const dialogEl = this.dialogEl.nativeElement;
    // Disable the CSS transition (see .scss) so each drag-driven inline
    // `transform` update below applies instantly instead of animating over
    // 200ms — was `style.animation = 'none'` when motion was keyframe-based.
    dialogEl.style.transition = 'none';
    const rect = dialogEl.getBoundingClientRect();
    // Natural top = current viewport top minus current drag offset
    const naturalTop = rect.top - this.dragOffset.y;

    this.dragStart = {
      mouseX: event.clientX,
      mouseY: event.clientY,
      offsetX: this.dragOffset.x,
      offsetY: this.dragOffset.y,
      naturalTop,
    };

    this.cleanupDragListeners();

    const moveUn = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => {
      const dx = e.clientX - this.dragStart.mouseX;
      const dy = e.clientY - this.dragStart.mouseY;
      const newX = this.dragStart.offsetX + dx;
      let newY = this.dragStart.offsetY + dy;

      // Apply minY constraint
      if (this.dragStart.naturalTop + newY < this.minY) {
        newY = this.minY - this.dragStart.naturalTop;
      }

      this.dragOffset = { x: newX, y: newY };
      this.applyDragTransform();
    });

    const upUn = this.renderer.listen('document', 'mouseup', () => {
      this.cleanupDragListeners();
    });

    this.dragUnlisteners.push(moveUn, upUn);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private applyDragTransform(): void {
    if (!this.dialogEl) return;
    const { x, y } = this.dragOffset;
    this.dialogEl.nativeElement.style.transform = `translate(${x}px, ${y}px)`;
  }

  private focusFirstElement(): void {
    if (!this.dialogEl) return;
    const first = this.getFocusableElements()[0];
    first?.focus();
  }

  private getFocusableElements(): HTMLElement[] {
    if (!this.dialogEl) return [];
    const selector = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');
    return Array.from(
      this.dialogEl.nativeElement.querySelectorAll<HTMLElement>(selector)
    );
  }

  private cleanupDragListeners(): void {
    this.dragUnlisteners.forEach(fn => fn());
    this.dragUnlisteners.length = 0;
  }

  /**
   * Cancels close()'s pending cleanup setTimeout, if any, and resets isClosing.
   * Must be called whenever the dialog is reopened or externally closed while
   * that timer is still pending — otherwise it fires later regardless of the
   * newer state change, re-running cleanup (moving the mask, resetting
   * z-index, forcing visible=false, re-emitting visibleChange/onHide) against
   * whatever open/close cycle happens to be current at that point.
   */
  private cancelPendingClose(): void {
    if (this.closeTimeoutId !== null) {
      clearTimeout(this.closeTimeoutId);
      this.closeTimeoutId = null;
    }
    this.isClosing = false;
  }

  /**
   * Returns the next z-index by scanning all currently open overlay masks
   * (both .p-drawer-mask and .p-dialog-mask) and adding 10 to the highest found.
   * This works across both hrone-drawer and hrone-dialog without any shared state.
   */
  private getNextZIndex(): number {
    const overlays = document.querySelectorAll<HTMLElement>('.p-drawer-mask, .p-dialog-mask');
    let max = this.baseZIndex;
    overlays.forEach(el => {
      const z = parseInt(el.style.zIndex || '0', 10);
      if (z > max) max = z;
    });
    return max + 10;
  }


}
